import type JSZip from "jszip";
import {
  readBoundedUtf8Stream,
  type PausableByteStream,
} from "./bounded-stream";
import { extractTextFromMarkup } from "./markup";
import type { DocumentExtraction, ExtractedDocumentSection } from "./types";

const MAX_ARCHIVE_ENTRIES = 5_000;
const MAX_CONTAINER_BYTES = 1024 * 1024;
const MAX_PACKAGE_BYTES = 2 * 1024 * 1024;
const MAX_CONTENT_DOCUMENT_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_CONTENT_DOCUMENT_BYTES = 25 * 1024 * 1024;
const CONTENT_MEDIA_TYPES = new Set(["application/xhtml+xml", "text/html"]);

interface ManifestItem {
  id: string;
  href: string;
  mediaType: string;
  fallback: string;
}

interface SizedZipEntry {
  _data?: {
    uncompressedSize?: number;
  };
}

interface StreamableZipEntry extends JSZip.JSZipObject {
  internalStream(type: "uint8array"): PausableByteStream;
}

function parseXml(value: string, label: string): XMLDocument {
  const document = new DOMParser().parseFromString(value, "application/xml");
  if (document.getElementsByTagNameNS("*", "parsererror").length > 0) {
    throw new Error(`Invalid ${label}.`);
  }
  return document;
}

function decodePath(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    throw new Error("Invalid encoded EPUB path.");
  }
}

function normalizeArchivePath(value: string): string {
  const normalized = decodePath(value).replace(/\\/g, "/");
  if (
    !normalized ||
    normalized.startsWith("/") ||
    normalized.startsWith("//") ||
    normalized.includes("\0") ||
    /^[a-z][a-z\d+.-]*:/i.test(normalized)
  ) {
    throw new Error("Invalid EPUB archive path.");
  }
  const parts: string[] = [];
  for (const part of normalized.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") {
      if (parts.length === 0) throw new Error("EPUB path leaves the archive.");
      parts.pop();
    } else {
      parts.push(part);
    }
  }
  if (parts.length === 0) throw new Error("Invalid EPUB archive path.");
  return parts.join("/");
}

function resolveArchivePath(baseFile: string, reference: string): string {
  const pathOnly = reference.split("#", 1)[0].split("?", 1)[0];
  const baseDirectory = baseFile.includes("/")
    ? baseFile.slice(0, baseFile.lastIndexOf("/") + 1)
    : "";
  return normalizeArchivePath(`${baseDirectory}${pathOnly}`);
}

function declaredSize(entry: JSZip.JSZipObject): number | null {
  const value = (entry as JSZip.JSZipObject & SizedZipEntry)._data
    ?.uncompressedSize;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

async function readArchiveText(
  archive: JSZip,
  path: string,
  maximumBytes: number,
): Promise<string> {
  const entry = archive.file(path);
  if (!entry || entry.dir) throw new Error(`Missing EPUB resource: ${path}`);
  const size = declaredSize(entry);
  if (size !== null && size > maximumBytes) {
    throw new Error(`EPUB resource is too large: ${path}`);
  }
  return readBoundedUtf8Stream(
    (entry as StreamableZipEntry).internalStream("uint8array"),
    maximumBytes,
    () => new Error(`EPUB resource is too large: ${path}`),
  );
}

function elements(document: XMLDocument, localName: string): Element[] {
  return Array.from(document.getElementsByTagNameNS("*", localName));
}

function resolveContentItem(
  id: string,
  manifest: Map<string, ManifestItem>,
): ManifestItem | null {
  const visited = new Set<string>();
  let item = manifest.get(id);
  while (item && !visited.has(item.id)) {
    visited.add(item.id);
    if (CONTENT_MEDIA_TYPES.has(item.mediaType)) return item;
    item = item.fallback ? manifest.get(item.fallback) : undefined;
  }
  return null;
}

function sectionLabel(index: number, title: string): string {
  return title ? `Section ${index + 1}: ${title}` : `Section ${index + 1}`;
}

export async function extractEpubText(file: File): Promise<DocumentExtraction> {
  const [{ default: JSZip }, arrayBuffer] = await Promise.all([
    import("jszip"),
    file.arrayBuffer(),
  ]);
  const archive = await JSZip.loadAsync(arrayBuffer);
  if (Object.keys(archive.files).length > MAX_ARCHIVE_ENTRIES) {
    throw new Error("EPUB contains too many archive entries.");
  }

  const container = parseXml(
    await readArchiveText(
      archive,
      "META-INF/container.xml",
      MAX_CONTAINER_BYTES,
    ),
    "EPUB container",
  );
  const rootfile = elements(container, "rootfile")[0];
  const packagePath = normalizeArchivePath(
    rootfile?.getAttribute("full-path") || "",
  );
  const packageDocument = parseXml(
    await readArchiveText(archive, packagePath, MAX_PACKAGE_BYTES),
    "EPUB package document",
  );

  const manifest = new Map<string, ManifestItem>();
  for (const item of elements(packageDocument, "item")) {
    const id = item.getAttribute("id") || "";
    const href = item.getAttribute("href") || "";
    if (!id || !href) continue;
    manifest.set(id, {
      id,
      href,
      mediaType: (item.getAttribute("media-type") || "").toLowerCase(),
      fallback: item.getAttribute("fallback") || "",
    });
  }

  const itemReferences = elements(packageDocument, "itemref");
  const primaryReferences = itemReferences.filter(
    (itemref) => itemref.getAttribute("linear")?.toLowerCase() !== "no",
  );
  const readingOrder =
    primaryReferences.length > 0 ? primaryReferences : itemReferences;
  const sections: ExtractedDocumentSection[] = [];
  const warnings: string[] = [];
  let totalDeclaredContentBytes = 0;
  let totalReadContentBytes = 0;

  for (const itemref of readingOrder) {
    const item = resolveContentItem(
      itemref.getAttribute("idref") || "",
      manifest,
    );
    if (!item) {
      warnings.push(
        "A reading-order section used an unsupported content type.",
      );
      continue;
    }
    const contentPath = resolveArchivePath(packagePath, item.href);
    const entry = archive.file(contentPath);
    if (!entry || entry.dir) {
      warnings.push("A reading-order section could not be found.");
      continue;
    }
    const size = entry ? declaredSize(entry) : null;
    if (size !== null) {
      totalDeclaredContentBytes += size;
      if (totalDeclaredContentBytes > MAX_TOTAL_CONTENT_DOCUMENT_BYTES) {
        throw new Error("EPUB reading-order content is too large.");
      }
    }
    const markup = await readArchiveText(
      archive,
      contentPath,
      MAX_CONTENT_DOCUMENT_BYTES,
    );
    totalReadContentBytes += new Blob([markup]).size;
    if (totalReadContentBytes > MAX_TOTAL_CONTENT_DOCUMENT_BYTES) {
      throw new Error("EPUB reading-order content is too large.");
    }
    const extracted = extractTextFromMarkup(markup);
    if (!extracted.text) continue;
    const index = sections.length;
    sections.push({
      id: `spine-${index + 1}`,
      label: sectionLabel(index, extracted.title),
      text: extracted.text,
    });
  }

  if (sections.length === 0) {
    throw new Error("EPUB does not contain readable spine content.");
  }
  return {
    text: sections.map((section) => section.text).join("\n\n"),
    sections,
    warnings,
  };
}
